import { useEffect, useMemo, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { useNavigate, useParams } from 'react-router-dom';
import { supabase, isSupabaseReady } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { BLOG_SECTIONS, BlogSection } from '@/lib/blogSections';

interface FormState {
  title: string;
  slug: string;
  excerpt: string;
  content_md: string;
  cover_image_url: string;
  author_name: string;
  section: BlogSection;
  tags: string; // comma separated
  status: 'draft' | 'published';
}

export default function BlogEditPage() {
  const { id } = useParams();
  const isNew = !id; // /system/blog/new has no id param
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState<FormState>({
    title: '',
    slug: '',
    excerpt: '',
    content_md: '',
    cover_image_url: '',
    author_name: '',
    section: 'Business',
    tags: '',
    status: 'draft',
  });

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (isNew || !isSupabaseReady) return;
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, content_md, cover_image_url, author_name, section, tags, status')
          .eq('id', id)
          .maybeSingle();
        if (error) throw error;
        if (data && !ignore) {
          setForm({
            title: data.title || '',
            slug: data.slug || '',
            excerpt: data.excerpt || '',
            content_md: data.content_md || '',
            cover_image_url: data.cover_image_url || '',
            author_name: data.author_name || '',
            section: (data.section as BlogSection) || 'Business',
            tags: Array.isArray(data.tags) ? (data.tags as string[]).join(', ') : (data.tags || ''),
            status: (data.status as 'draft' | 'published') || 'draft',
          });
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [id, isNew]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const generateSlug = () => {
    if (!form.title) return;
    const s = form.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
    setForm((f) => ({ ...f, slug: s }));
  };

  const canSave = useMemo(() => {
    return form.title.trim().length > 0 && form.slug.trim().length > 0;
  }, [form.title, form.slug]);

  const stripMarkdown = (md: string): string => {
    return md
      .replace(/```[\s\S]*?```/g, ' ')          // code fences
      .replace(/`[^`]*`/g, ' ')                  // inline code
      .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ') // images
      .replace(/\[[^\]]*\]\([^)]*\)/g, '$1') // links -> text
      .replace(/^#+\s*(.*)$/gm, '$1')           // headings
      .replace(/[*_~>#>-]/g, ' ')                // md symbols
      .replace(/\s+/g, ' ')                     // collapse whitespace
      .trim();
  };

  const onSave = async (publish = false) => {
    setError(null);
    setSaved(false);
    setLoading(true);
    try {
      if (!isSupabaseReady) throw new Error('Supabase is not configured');
      // Auto-generate excerpt if empty
      const derivedExcerpt = form.excerpt.trim() || stripMarkdown(form.content_md).slice(0, 160).trim();

      const payload: any = {
        title: form.title.trim(),
        slug: form.slug.trim(),
        excerpt: derivedExcerpt,
        content_md: form.content_md,
        cover_image_url: form.cover_image_url.trim() || null,
        author_name: form.author_name.trim() || null,
        section: form.section,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        status: publish ? 'published' : form.status,
        published_at: publish ? new Date().toISOString() : null,
      };

      if (isNew) {
        const { data, error } = await supabase.from('blog_posts').insert(payload).select('id').single();
        if (error) throw error;
        setSaved(true);
        navigate(`/system/blog/${data!.id}`);
      } else {
        const { error } = await supabase.from('blog_posts').update(payload).eq('id', id);
        if (error) throw error;
        setSaved(true);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!id) return;
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);
      if (error) throw error;
      navigate('/system/blog');
    } catch (e: any) {
      setError(e.message || 'Failed to delete');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{isNew ? 'New Post' : 'Edit Post'}</h1>
          <p className="text-sm text-gray-600">Posts are global and will appear across all tenants.</p>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <>
              <Button variant="secondary" onClick={() => navigate('/system/blog')}>Back</Button>
              <Button variant="destructive" disabled={loading} onClick={onDelete}>Delete</Button>
            </>
          )}
          <Button variant="outline" onClick={generateSlug}>Generate slug</Button>
          <Button disabled={!canSave || loading} onClick={() => onSave(false)}>Save draft</Button>
          <Button disabled={!canSave || loading} onClick={() => onSave(true)}>Publish</Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</div>
      )}
      {saved && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">Saved</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input name="title" value={form.title} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Slug</label>
            <input name="slug" value={form.slug} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Excerpt</label>
            <textarea name="excerpt" value={form.excerpt} onChange={handleChange} rows={3} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
	          <div className="mt-3">
	            <div className="text-xs font-medium text-gray-600 mb-2">Live preview</div>
	            <div className="prose prose-gray max-w-none whitespace-pre-line prose-headings:mt-6 prose-headings:mb-3 prose-p:my-3 rounded-md border bg-gray-50 p-4">
	              <ReactMarkdown remarkPlugins={[remarkGfm]}>
	                {form.content_md || 'Start typing markdown above to see a live preview.'}
	              </ReactMarkdown>
	            </div>
	          </div>

            <label className="block text-sm font-medium text-gray-700">Content (Markdown)</label>
            <textarea name="content_md" value={form.content_md} onChange={handleChange} rows={12} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono" />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Author name</label>
            <input name="author_name" value={form.author_name} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Section</label>
            <select name="section" value={form.section} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              {BLOG_SECTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Cover image</label>
            <p className="mt-1 text-xs text-gray-500">Recommended 1600×900 (16:9). JPG/PNG/WebP. Aim for ≤ 500 KB.</p>
            <div className="mt-2 flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploadingCover(true);
                  try {
                    // Inspect image dimensions for guidance (non-blocking)
                    const objectUrl = URL.createObjectURL(file);
                    await new Promise<void>((resolve) => {
                      const img = new Image();
                      img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(); };
                      img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(); };
                      img.src = objectUrl;
                    });

                    const path = `covers/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
                    const { error: upErr } = await supabase.storage.from('blog-images').upload(path, file, { upsert: true });
                    if (upErr) throw upErr;
                    const { data } = supabase.storage.from('blog-images').getPublicUrl(path);
                    setForm((f) => ({ ...f, cover_image_url: data.publicUrl }));
                  } catch (e) {
                    setError((e as any)?.message || 'Upload failed');
                  } finally {
                    setUploadingCover(false);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }
                }}
              />
              <Button type="button" variant="outline" disabled={uploadingCover} onClick={() => fileInputRef.current?.click()}>
                {uploadingCover ? 'Uploading…' : 'Upload image'}
              </Button>
              {form.cover_image_url && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      const url = form.cover_image_url;
                      const m = url.match(/\/object\/public\/blog-images\/(.+)$/);
                      if (m && m[1]) {
                        await supabase.storage.from('blog-images').remove([m[1]]);
                      }
                      setForm((f) => ({ ...f, cover_image_url: '' }));
                    } catch (err) {
                      setError((err as any)?.message || 'Failed to delete image');
                    }
                  }}
                >
                  Remove
                </Button>
              )}
              <input
                name="cover_image_url"
                value={form.cover_image_url}
                onChange={handleChange}
                placeholder="Or paste a public image URL"
                className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            {form.cover_image_url && (
              <div className="mt-2 rounded-md overflow-hidden bg-gray-100">
                <img src={form.cover_image_url} className="w-full object-cover" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags (comma separated)</label>
            <input name="tags" value={form.tags} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select name="status" value={form.status} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

